import { FallbackSectionErrorBoundary } from "@/components/FallbackErrorBoundary";
import { HeadingSection } from "@/components/heading/HeadingSection";
import { DrawingsSection } from "@/features/drawings/DrawingsSection";
import { FilesSection } from "@/features/files/FilesSection";
import { DraftOrdersSection } from "@/features/orders/draft/DraftOrdersSection";
import { InstallationQuotationSection } from "@/features/orders/installationQuotation/InstallationQuotationSection";
import { OrdersSection } from "@/features/orders/order/OrdersSection";
import { QuotationsSection } from "@/features/orders/quotation/QuotationsSection";
import { TaskSection } from "@/features/project/TaskSection";
import { ProjectTopSection } from "@/features/project/projectTopSection/ProjectTopSection";
import { STAGE_ACTIONS_SECTION_IDS } from "@/features/project/projectTopSection/stages/config";
import { useHideSubHeader } from "@/hooks/useSubHeaderVisibility";
import { t } from "@repo/translation";
import {
  CardContainer,
  PaddedContainer,
  TwoColumnSectionContainer,
  useSettings,
} from "@repo/ui";
import { hasItems } from "@repo/utils";

import { HomeProjectSection } from "../homeProject/HomeProjectSection";
import { WorkOrdersSection } from "../orders/workOrders/WorkOrdersSection";
import { useActiveProject } from "./ActiveProject";
import { ProjectDetailsSection } from "./ProjectDetailsSection";

export const ManageProject = () => {
  useHideSubHeader();
  const { opportunity } = useActiveProject();
  const { quotation, feature_flags } = useSettings();

  if (!opportunity) {
    return null;
  }

  const editable = !opportunity?.lockStatus;

  const showQuotationSection = quotation?.enable_quotations_import;
  const showHomeProjectSection =
    hasItems(opportunity?.customerProjects) &&
    hasItems(opportunity?.prePlanningDetails);
  const showWOIQSection =
    feature_flags?.enable_salesforce_integration &&
    opportunity?.hfb?.includes("hfb07");

  return (
    <>
      <ProjectTopSection />

      <HeadingSection>
        <ProjectDetailsSection />

        <PaddedContainer>
          <TwoColumnSectionContainer>
            <CardContainer id={STAGE_ACTIONS_SECTION_IDS.LEFT_SECTION}>
              {showWOIQSection && (
                <InstallationQuotationSection editable={editable} />
              )}
              <FallbackSectionErrorBoundary
                errorMessage={t("prompt.failed_to_load_work_orders")}
              >
                {showWOIQSection && <WorkOrdersSection editable={editable} />}
              </FallbackSectionErrorBoundary>

              {showHomeProjectSection && (
                <FallbackSectionErrorBoundary
                  errorMessage={t("prompt.failed_to_load_home_project")}
                >
                  <HomeProjectSection />
                </FallbackSectionErrorBoundary>
              )}
              <FallbackSectionErrorBoundary
                errorMessage={t("prompt.failed_to_load_orders")}
              >
                <OrdersSection editable={editable} />
              </FallbackSectionErrorBoundary>
              {showQuotationSection && (
                <FallbackSectionErrorBoundary
                  errorMessage={t("prompt.failed_to_load_quotations")}
                >
                  <QuotationsSection editable={editable} />
                </FallbackSectionErrorBoundary>
              )}
              <FallbackSectionErrorBoundary
                errorMessage={t("prompt.failed_to_load_draft_orders")}
              >
                <DraftOrdersSection editable={editable} />
              </FallbackSectionErrorBoundary>
              <FallbackSectionErrorBoundary
                errorMessage={t("prompt.failed_to_load_drawings")}
              >
                <DrawingsSection editable={editable} />
              </FallbackSectionErrorBoundary>
              <FallbackSectionErrorBoundary
                errorMessage={t("prompt.failed_to_load_files")}
              >
                <FilesSection opportunity={opportunity} editable={editable} />
              </FallbackSectionErrorBoundary>
            </CardContainer>
            <CardContainer>
              <FallbackSectionErrorBoundary
                errorMessage={t("prompt.failed_to_load_tasks")}
              >
                <TaskSection opportunity={opportunity} editable={editable} />
              </FallbackSectionErrorBoundary>
            </CardContainer>
          </TwoColumnSectionContainer>
        </PaddedContainer>
      </HeadingSection>
    </>
  );
};
